/* eslint-disable react/no-unescaped-entities, no-empty */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart, useAuth } from '../App';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { Button } from '../components/ui/button';
import SquareCardForm from '../components/SquareCardForm';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { 
  ShoppingCart, 
  Minus, 
  Plus, 
  Trash2, 
  ArrowRight,
  Shield,
  ArrowLeft,
  CheckSquare,
  Square,
  Package,
  Clock,
  AlertCircle,
  Tag,
  Percent,
  X,
  Truck,
  MapPin,
  Calendar,
  DollarSign,
  Award,
  LogIn
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SQUARE_APP_ID = process.env.REACT_APP_SQUARE_APPLICATION_ID;
const SQUARE_LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID;
const SQUARE_ENV = process.env.REACT_APP_SQUARE_ENVIRONMENT || 'production';

const CartPage = () => {
  const { items, updateQuantity, removeItem, clearCart, total, itemCount, addItem, cartCountdownEnabled } = useCart();
  const { user, token } = useAuth();
  const { settings: siteSettings } = useSiteSettings();
  const onlinePaymentsEnabled = siteSettings?.online_payments_enabled !== false;
  const paymentsDisabledMessage = siteSettings?.payments_disabled_message
    || 'Online payments are temporarily unavailable. Please contact us to complete your order.';
  const contactEmail = siteSettings?.contact_email || '';
  // Cash App is a manual stopgap: customer scans the QR, sends payment from
  // their Cash App, then we mark them paid and ship. Toggled via admin Site
  // Settings, available alongside Square (or as fallback).
  const cashappEnabled = !!siteSettings?.cashapp_enabled;
  const cashappHandle = siteSettings?.cashapp_handle || '';
  const cashappQrUrl = siteSettings?.cashapp_qr_url || '';
  const cashappNote = siteSettings?.cashapp_note || '';
  const [showCashApp, setShowCashApp] = React.useState(false);
  const [cashAppOrderId, setCashAppOrderId] = React.useState(null);
  const [cashAppSubmitting, setCashAppSubmitting] = React.useState(false);
  // Zelle — same workflow as Cash App, just a different processor. The
  // customer scans the QR (or sends to the email handle), then clicks "I've
  // sent the payment"; admin verifies the deposit and confirms.
  const zelleEnabled = !!siteSettings?.zelle_enabled;
  const zelleHandle = siteSettings?.zelle_handle || '';
  const zelleRecipientName = siteSettings?.zelle_recipient_name || '';
  const zelleQrUrl = siteSettings?.zelle_qr_url || '';
  const zelleNote = siteSettings?.zelle_note || '';
  const [showZelle, setShowZelle] = React.useState(false);
  const [zelleOrderId, setZelleOrderId] = React.useState(null);
  const [zelleSubmitting, setZelleSubmitting] = React.useState(false);
  // Name + phone/email tied to the customer's Zelle account, so the team can
  // send a Zelle *request* for the reserved amount.
  const [zelleSenderName, setZelleSenderName] = React.useState('');
  const [zelleSenderContact, setZelleSenderContact] = React.useState('');
  // Authoritative server-side quotes for the Cash App / Zelle "Amount" pills.
  // Without this the pill depends on `appliedDiscount` React state, which
  // can briefly be empty during the pricing-info fetch race — and customers
  // were sending the WRONG amount (pre-tier-discount) because the UI hadn't
  // caught up yet. We re-fetch a quote any time the cart or payment method
  // changes; if the fetch is pending or fails we fall back to the local
  // `finalTotal` calc so the pill is never blank.
  const [cashAppQuote, setCashAppQuote] = React.useState(null);
  const [zelleQuote, setZelleQuote] = React.useState(null);
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [researchConsent, setResearchConsent] = React.useState(false);
  const [pricingInfo, setPricingInfo] = React.useState(null);
  const [discountCode, setDiscountCode] = React.useState('');
  // The validated affiliate/referral code the customer entered. Tracked
  // SEPARATELY from `appliedDiscount` (which drives the displayed price) so the
  // affiliate is always credited — even when the customer's own pricing tier
  // happens to give an equal/larger discount and "wins" the price comparison.
  const [referralCode, setReferralCode] = React.useState('');
  const [appliedDiscount, setAppliedDiscount] = React.useState(null);
  const [applyingCode, setApplyingCode] = React.useState(false);
  const [deliveryOptions, setDeliveryOptions] = React.useState(null);
  const [deliveryMethod, setDeliveryMethod] = React.useState('shipping');
  const [pickupDate, setPickupDate] = React.useState('');
  const [pickupTime, setPickupTime] = React.useState('');
  const [availableDates, setAvailableDates] = React.useState([]);
  const [availableSlots, setAvailableSlots] = React.useState([]);
  const [loadingDates, setLoadingDates] = React.useState(false);
  const [loadingSlots, setLoadingSlots] = React.useState(false);
  const [creditAmount, setCreditAmount] = React.useState(null); // null = use all, number = specific amount
  const [useCredit, setUseCredit] = React.useState(true);
  const [stockValidating, setStockValidating] = React.useState(false);
  const [stockIssues, setStockIssues] = React.useState([]);
  // Persistent, unmissable error shown when a Cash App / Zelle reservation
  // FAILS to be created (stock changed, session expired, network, etc.). A
  // disappearing toast was too easy to miss — customers were sending payment
  // for orders that never landed in the admin panel.
  const [reserveError, setReserveError] = React.useState(null);
  // Shipping address state — required for ALL orders going forward.
  // Customers need to be reachable (phone), and we record an address for
  // every order for compliance + customer history, even pickup orders.
  const [shippingAddress, setShippingAddress] = React.useState({
    name: '', address_line1: '', address_line2: '', city: '', state: '', zip_code: '', country: 'US'
  });
  const [phone, setPhone] = React.useState('');
  const [savedAddresses, setSavedAddresses] = React.useState([]);
  React.useEffect(() => {
    if (!token) return;
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/addresses`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const list = await res.json();
        setSavedAddresses(list || []);
        // Auto-fill from default address if cart shipping form is still empty
        const def = (list || []).find(a => a.is_default) || (list || [])[0];
        if (def) {
          setShippingAddress(prev => prev.address_line1 ? prev : {
            name: user?.name || '',
            address_line1: def.street || '',
            address_line2: def.street2 || '',
            city: def.city || '',
            state: def.state || '',
            zip_code: def.zip_code || '',
            country: (def.country === 'United States' ? 'US' : (def.country || 'US')),
          });
        } else if (user?.name) {
          setShippingAddress(prev => prev.name ? prev : { ...prev, name: user.name });
        }
        // Best-effort: pre-fill phone from customer profile
        try {
          const profRes = await fetch(`${API_URL}/api/profile`, { headers: { Authorization: `Bearer ${token}` } });
          if (profRes.ok) {
            const p = await profRes.json();
            if (p?.phone) setPhone(prev => prev || p.phone);
          }
        } catch (_) { /* silent */ }
      } catch (_) { /* silent */ }
    };
    load();
     
  }, [token]);
  const shippingAddressValid = !!(
    shippingAddress.name?.trim() &&
    shippingAddress.address_line1?.trim() &&
    shippingAddress.city?.trim() &&
    shippingAddress.state?.trim() &&
    shippingAddress.zip_code?.trim()
  );
  // Phone is now required on every order (cash pickup, shipping, Cash App,
  // Reserve). At least 10 digits after stripping non-numerics.
  const phoneValid = ((phone || '').replace(/\D/g, '').length >= 10);
  // Map of product_id -> { member_discount_eligible, any_variant_on_sale, variants[].is_on_sale }
  // Used to enrich cart items added before the eligibility flags existed in the cart payload.
  const [productFlags, setProductFlags] = React.useState({});
  // Re-render the cart every 10s so each line's expiry countdown stays fresh.
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 10 * 1000);
    return () => clearInterval(id);
  }, []);

  // "Don't forget your supplies" reminder data — only fetched when user is logged in
  // (the /shop endpoint requires auth). Categories: 'supplies' (BAC), 'cartridges', 'pens'.
  const [shopSupplies, setShopSupplies] = React.useState([]);
  React.useEffect(() => {
    if (!token) return;
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/shop/products`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const all = await res.json();
        const wantedCategories = new Set(['supplies', 'cartridges', 'pens']);
        const matches = (all || []).filter(p => wantedCategories.has((p.category || '').toLowerCase()) && p.visible);
        setShopSupplies(matches);
      } catch (_) {/* silent */}
    };
    load();
  }, [token]);

  // Detect missing supplies: only nudge ONCE for BAC. If cart already has a BAC line,
  // we hide the entire reminder (per user choice — no repeated nags for cartridges/pen).
  const reminder = React.useMemo(() => {
    if (!shopSupplies.length) return null;
    const cartHasPeptide = items.some(it => (it.item_type !== 'shop'));
    if (!cartHasPeptide) return null;
    const cartShopIds = new Set(items.filter(it => it.item_type === 'shop').map(it => it.product_id));
    const bac = shopSupplies.find(p => (p.category || '').toLowerCase() === 'supplies');
    if (!bac) return null;
    const cartHasBac = cartShopIds.has(bac.product_id);
    if (cartHasBac) return null; // user already added BAC — don't nag for the rest
    // Build the suggestion list (BAC, Cartridges, Pen) — only those NOT in cart
    const suggestions = shopSupplies
      .filter(p => !cartShopIds.has(p.product_id))
      .sort((a, b) => {
        const order = { supplies: 0, cartridges: 1, pens: 2 };
        return (order[(a.category || '').toLowerCase()] ?? 99) - (order[(b.category || '').toLowerCase()] ?? 99);
      });
    return suggestions.length ? suggestions : null;
  }, [items, shopSupplies]);

  // Quick-add a shop supply to the cart with sensible defaults.
  const handleQuickAddSupply = (p) => {
    // Pick the first available variant (color or pack_size). Falls back to base_price.
    let price = Number(p.base_price ?? p.price ?? 0);
    let variantPayload = {};
    if (p.has_pack_sizes && Array.isArray(p.pack_sizes) && p.pack_sizes.length) {
      const ps = p.pack_sizes.find(x => (x.stock_quantity ?? 0) > 0) || p.pack_sizes[0];
      price = Number(ps.price ?? price);
      variantPayload = { pack_size: ps.quantity ?? ps.name };
    } else if (p.has_colors && Array.isArray(p.colors) && p.colors.length) {
      const c = p.colors.find(x => (x.stock_quantity ?? 0) > 0) || p.colors[0];
      variantPayload = { color: c.name };
    }
    addItem({
      product_id: p.product_id,
      name: p.name,
      price,
      image_url: p.image_url,
      item_type: 'shop',
      discount_eligible: !!p.discount_eligible,
      stock_quantity: p.stock_quantity || 0,
      ...variantPayload,
    }, 1);
    toast.success(`${p.name} added to your cart`);
  };

  // Fetch peptide product eligibility flags so stale cart items still get the right exclusion
  // applied for member-tier discounts and discount codes (no double-dipping).
  React.useEffect(() => {
    const fetchFlags = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await fetch(`${API_URL}/api/products`, { headers });
        if (!response.ok) return;
        const data = await response.json();
        const map = {};
        data.forEach(p => {
          map[p.product_id] = {
            member_discount_eligible: p.member_discount_eligible !== false,
            any_variant_on_sale: !!p.any_variant_on_sale,
            variants: (p.variants || []).map(v => ({ is_on_sale: !!v.is_on_sale, price: Number(v.price || 0) }))
          };
        });
        setProductFlags(map);
      } catch (e) {
        // Network error — fallback to per-item flags only
      }
    };
    fetchFlags();
  }, [token]);

  // Returns true if this cart item participates in the applied tier/code discount.
  // Sale items are NOT excluded here — best-price-wins logic in
  // calculateDiscountAmount decides whether the sale price or the discounted
  // (off-original) price is cheaper for the customer.
  const isItemDiscountEligible = (item) => {
    // Shop accessories never participate in member/code discounts — this mirrors
    // the authoritative backend /orders/reserve + checkout so the cart total,
    // the Zelle/Cash App quote, and the order email always agree.
    if (item.item_type === 'shop') return false;
    // Per-item flag wins if present (set when adding from ProductDetailPage).
    if (item.member_discount_eligible === false) return false;
    // Fall back to product flags fetched on cart load (handles stale cart items).
    const baseId = (item.product_id || '').split('_').slice(0, -1).join('_') || item.product_id;
    const flags = productFlags[baseId];
    if (flags && flags.member_discount_eligible === false) return false;
    return true;
  };

  // The pre-sale unit price for a cart item (falls back to its current price).
  const getOriginalUnitPrice = (item) => {
    const cur = item.price || 0;
    if (item.original_price) return Math.max(item.original_price, cur);
    const baseId = (item.product_id || '').split('_').slice(0, -1).join('_') || item.product_id;
    const vIdx = parseInt(item.variant_index, 10);
    const flags = productFlags[baseId];
    const vPrice = flags && !isNaN(vIdx) ? flags.variants?.[vIdx]?.price : 0;
    return Math.max(vPrice || cur, cur);
  };

  // Calculate total items in cart
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  // Fetch user's pricing tier info
  React.useEffect(() => {
    const fetchPricing = async () => {
      if (!token) return; // Only fetch if user is logged in
      try {
        const response = await fetch(`${API_URL}/api/my-pricing`, { 
          
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (response.ok) {
          const data = await response.json();
          setPricingInfo(data);
          // Auto-apply tier discount
          if (data.discount && data.discount.value > 0) {
            setAppliedDiscount({
              source: 'tier',
              name: data.tier.name,
              type: data.discount.type,
              value: data.discount.value
            });
          }
        }
      } catch (error) {
        console.error('Error fetching pricing:', error);
      }
    };
    fetchPricing();
  }, [token]);

  // Fetch delivery options
  React.useEffect(() => {
    const fetchDeliveryOptions = async () => {
      try {
        const response = await fetch(`${API_URL}/api/checkout/delivery-options`, { 
          
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (response.ok) {
          const data = await response.json();
          setDeliveryOptions(data);
        }
      } catch (error) {
        console.error('Error fetching delivery options:', error);
      }
    };
    fetchDeliveryOptions();
  }, [token]);

  // Re-validate stock for every cart line against the live DB. Returns the
  // list of blocking issues AND sets state so the UI reflects them. This
  // mirrors the backend `validate_stock_for_items` gate EXACTLY (OOS allowed
  // only when the product opts into pre-orders, or the line is a pre-order),
  // so the cart never lets a customer reach "send payment" for a line that
  // /orders/reserve would reject with a 400.
  const revalidateStock = React.useCallback(async () => {
    if (items.length === 0) {
      setStockIssues([]);
      return [];
    }
    setStockValidating(true);
    try {
      const response = await fetch(`${API_URL}/api/cart/validate-stock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!response.ok) return [];
      const data = await response.json();
      const issues = [];
      data.items.forEach((vi) => {
        const isPreorder = !!vi.is_preorder;
        const allowPre = !!vi.allow_preorder;
        if (vi.stock_status === 'not_found' || vi.stock_status === 'variant_not_found') {
          // Product/variant no longer exists (renamed or removed). The backend
          // would 400 ("Invalid vial size") at submit, so block it up front.
          issues.push({
            product_id: vi.product_id,
            name: vi.name || 'Product',
            vial_size: vi.vial_size,
            status: 'unavailable',
            available: 0,
          });
        } else if (vi.stock_status === 'out_of_stock' && !isPreorder && !allowPre) {
          issues.push({
            product_id: vi.product_id,
            name: vi.name || 'Product',
            vial_size: vi.vial_size,
            status: 'out_of_stock',
            available: 0,
          });
        } else if (vi.stock_status === 'in_stock' && vi.quantity > vi.stock_quantity && !isPreorder) {
          issues.push({
            product_id: vi.product_id,
            name: vi.name || 'Product',
            vial_size: vi.vial_size,
            status: 'insufficient',
            available: vi.stock_quantity,
            requested: vi.quantity,
          });
        }
      });
      setStockIssues(issues);
      return issues;
    } catch (error) {
      console.error('Error validating stock:', error);
      return [];
    } finally {
      setStockValidating(false);
    }
     
  }, [items]);

  // Validate stock whenever the cart contents change.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const issues = await revalidateStock();
      if (!cancelled && issues.length > 0) {
        toast.warning('Some items in your cart have stock changes. Please review before checkout.');
      }
    })();
    return () => { cancelled = true; };
  }, [revalidateStock]);

  // Fetch available pickup dates from Google Calendar
  React.useEffect(() => {
    if (deliveryMethod !== 'local_pickup') return;
    const fetchDates = async () => {
      setLoadingDates(true);
      try {
        const res = await fetch(`${API_URL}/api/pickup/available-dates`);
        if (res.ok) {
          const data = await res.json();
          setAvailableDates(data.available_dates || []);
        }
      } catch (e) {
        console.error('Error fetching pickup dates:', e);
      } finally {
        setLoadingDates(false);
      }
    };
    fetchDates();
  }, [deliveryMethod]);

  // Fetch available time slots when a pickup date is selected
  React.useEffect(() => {
    if (!pickupDate || deliveryMethod !== 'local_pickup') return;
    setPickupTime('');
    const fetchSlots = async () => {
      setLoadingSlots(true);
      try {
        const res = await fetch(`${API_URL}/api/pickup/available-slots?date=${pickupDate}`);
        if (res.ok) {
          const data = await res.json();
          setAvailableSlots(data.slots || []);
          if (!data.available) {
            toast.error(data.reason || 'No slots available for this date');
          }
        }
      } catch (e) {
        console.error('Error fetching pickup slots:', e);
      } finally {
        setLoadingSlots(false);
      }
    };
    fetchSlots();
  }, [pickupDate, deliveryMethod]);

  // Calculate shipping cost: free for pickup/cash-in-person, otherwise flat $10.
  // Free shipping is disabled by default (threshold 0); admins can re-enable it
  // by setting a free_shipping_threshold > 0 in Site Settings.
  const freeShipThreshold = Number(siteSettings?.free_shipping_threshold ?? 0);
  const qualifiesFreeShipping = freeShipThreshold > 0 && total >= freeShipThreshold;
  const shippingCost = (deliveryMethod === 'local_pickup' || deliveryMethod === 'cash_payment' || qualifiesFreeShipping) ? 0 : 10;

  // Calculate discount amount with best-price-wins on sale items.
  // For percentage discounts the discount is computed off each item's ORIGINAL
  // (pre-sale) price and the customer pays the lower of the sale price and the
  // tier/code price. Mirrors the backend pricing.member_discount_total helper.
  // For fixed-amount discounts, sale items are excluded (no stacking on a sale).
  const calculateDiscountAmount = () => {
    if (!appliedDiscount) return 0;
    if (appliedDiscount.type === 'percentage') {
      const rate = appliedDiscount.value / 100;
      return items.reduce((sum, item) => {
        if (!isItemDiscountEligible(item)) return sum;
        const qty = item.quantity || 1;
        const cur = item.price || 0;
        const orig = getOriginalUnitPrice(item);
        const discountedUnit = orig * (1 - rate);
        const newUnit = Math.min(cur, discountedUnit);
        return sum + (newUnit < cur ? (cur - newUnit) * qty : 0);
      }, 0);
    }
    const eligibleTotal = items.reduce((sum, item) => {
      if (!isItemDiscountEligible(item) || item.is_on_sale) return sum;
      return sum + ((item.price || 0) * (item.quantity || 1));
    }, 0);
    return Math.min(appliedDiscount.value, eligibleTotal);
  };

  const discountAmount = calculateDiscountAmount();
  const finalTotal = total - discountAmount + shippingCost;

  // Check if wholesale tier and meets minimum
  const isWholesale = pricingInfo?.tier?.name?.toLowerCase().includes('wholesale');
  const minQuantity = pricingInfo?.tier?.min_quantity || 0;
  const meetsMinimum = !minQuantity || totalItems >= minQuantity;
  const leadTimeDays = pricingInfo?.tier?.lead_time_days;

  // Apply discount code
  const handleApplyDiscountCode = async () => {
    if (!discountCode.trim()) {
      toast.error('Please enter a discount code');
      return;
    }

    setApplyingCode(true);
    try {
      const response = await fetch(`${API_URL}/api/validate-discount-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ code: discountCode.trim(), cart_total: total })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Invalid discount code');
      }

      // The code is valid → always record it for affiliate attribution,
      // regardless of whether it or the tier produces the bigger discount.
      setReferralCode(discountCode.trim().toUpperCase());

      // Check if code discount is better than tier discount
      const codeDiscountAmount = data.discount_amount;
      const tierDiscountAmount = pricingInfo?.discount?.value 
        ? (pricingInfo.discount.type === 'percentage' 
            ? total * (pricingInfo.discount.value / 100) 
            : pricingInfo.discount.value)
        : 0;

      if (codeDiscountAmount > tierDiscountAmount) {
        setAppliedDiscount({
          source: 'code',
          code: discountCode.trim().toUpperCase(),
          type: data.discount_type,
          value: data.discount_value
        });
        toast.success(`Discount code applied! You save $${codeDiscountAmount.toFixed(2)}`);
      } else if (tierDiscountAmount > 0) {
        toast.info(`Your ${pricingInfo.tier.name} tier already gives you a better discount!`);
      } else {
        setAppliedDiscount({
          source: 'code',
          code: discountCode.trim().toUpperCase(),
          type: data.discount_type,
          value: data.discount_value
        });
        toast.success(`Discount code applied! You save $${codeDiscountAmount.toFixed(2)}`);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setApplyingCode(false);
    }
  };

  // Remove discount code
  const handleRemoveDiscount = () => {
    // If user has tier discount, revert to that
    if (pricingInfo?.discount?.value > 0) {
      setAppliedDiscount({
        source: 'tier',
        name: pricingInfo.tier.name,
        type: pricingInfo.discount.type,
        value: pricingInfo.discount.value
      });
    } else {
      setAppliedDiscount(null);
    }
    setDiscountCode('');
    setReferralCode('');
  };

  // Synchronous client-side validator. Returns true if checkout can proceed.
  // Apple Pay's tokenize() must be called from a sync user gesture, so we
  // can't rely on the async handleCheckout() for the wallet flows.
  const canCheckoutSync = () => {
    if (items.length === 0) {
      toast.error('Your cart is empty');
      return false;
    }
    if (!researchConsent) {
      toast.error('Please confirm that you are purchasing for research purposes only');
      return false;
    }
    if ((deliveryMethod === 'local_pickup' || deliveryMethod === 'cash_payment') && !pickupDate) {
      toast.error('Please select a preferred pickup date');
      return false;
    }
    if (deliveryMethod === 'local_pickup' && !pickupTime) {
      toast.error('Please select a pickup time slot');
      return false;
    }
    // Phone + address are required on EVERY order — we need to be able to
    // reach customers regardless of delivery method.
    if (!phoneValid) {
      toast.error('Please enter a valid phone number (at least 10 digits).');
      return false;
    }
    if (!shippingAddressValid) {
      toast.error('Please enter a complete address (name, street, city, state, ZIP).');
      return false;
    }
    if (isWholesale && !meetsMinimum) {
      toast.error(`Wholesale orders require a minimum of ${minQuantity} items. You have ${totalItems} items.`);
      return false;
    }
    return true;
  };

  const handleCheckout = async () => {
    if (!canCheckoutSync()) return;

    // For cash payment, process directly through PayPal endpoint  
    if (deliveryMethod === 'cash_payment') {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/paypal/create-order`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify(getCheckoutPayload())
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          if (response.status === 409 && errorData?.detail?.stock_issues) {
            handleStockError(errorData.detail);
            return;
          }
          throw new Error(errorData?.detail || 'Failed to create order');
        }
        const data = await response.json();
        if (data.is_free_order && data.checkout_url) {
          toast.success('Order placed successfully! Redirecting...');
          window.location.href = data.checkout_url;
        }
      } catch (error) {
        console.error('Checkout error:', error);
        toast.error('Failed to place order. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }
    
    return true; // Signal that validation passed for PayPal
  };

  const getCheckoutPayload = () => ({
    origin_url: window.location.origin,
    items: items.map(item => ({
      product_id: item.product_id,
      quantity: item.quantity,
      vial_size: item.vial_size,
      price: item.price,
      // Per-item preorder flag — backend uses this to skip inventory deduction
      // for ONLY the preorder lines (so an in-stock peptide + a preorder peptide
      // can ship in the same order; preorder line gets deducted at fulfillment).
      is_preorder: !!item.is_preorder,
      ...(item.item_type === 'shop' ? {
        item_type: 'shop',
        color: item.color,
        pack_size: item.pack_size
      } : {})
    })),
    research_consent: true,
    consent_timestamp: new Date().toISOString(),
    is_wholesale: isWholesale,
    discount_code: referralCode || (appliedDiscount?.source === 'code' ? appliedDiscount.code : ''),
    delivery_method: deliveryMethod,
    pickup_date: deliveryMethod === 'local_pickup' ? pickupDate : null,
    pickup_time: deliveryMethod === 'local_pickup' ? pickupTime : null,
    is_preorder: items.some(item => item.is_preorder),
    store_credit_amount: user?.store_credit > 0 ? (useCredit ? (creditAmount !== null ? creditAmount : user.store_credit) : 0) : 0,
    // Address + phone are required on every order regardless of delivery
    // method. Address is sent for all paths so admin always has a record.
    shipping_address: shippingAddress,
    phone: phone.trim(),
  });

  // Authoritative server quotes for Cash App / Zelle Amount pills. Fired
  // whenever the cart contents, discount, delivery, or store-credit choice
  // change. If the request fails we leave the existing quote (and the UI
  // gracefully falls back to the local calc).
  const fetchPaymentQuote = React.useCallback(async (paymentMethod) => {
    if (!items || items.length === 0) return null;
    try {
      const payload = { ...getCheckoutPayload(), payment_method: paymentMethod };
      const res = await fetch(`${API_URL}/api/orders/quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
     
  }, [items, appliedDiscount, deliveryMethod, useCredit, creditAmount, shippingAddress, phone, token]);

  // Refresh both quotes whenever the inputs change.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cashappEnabled) {
        const q = await fetchPaymentQuote('cash_app');
        if (!cancelled && q) setCashAppQuote(q);
      }
      if (zelleEnabled) {
        const q = await fetchPaymentQuote('zelle');
        if (!cancelled && q) setZelleQuote(q);
      }
    })();
    return () => { cancelled = true; };
  }, [fetchPaymentQuote, cashappEnabled, zelleEnabled]);

  const handleStockError = (detail) => {
    const issues = detail.stock_issues;
    setStockIssues(issues.map(si => ({
      product_id: si.product_id,
      name: si.name,
      vial_size: si.vial_size,
      status: si.issue,
      available: si.available,
      requested: si.requested
    })));
    toast.error(detail.message || 'Stock has changed. Please update your cart.');
    setLoading(false);
  };

  // Square pre-tokenize validation gate. Returns true if we should proceed.
  // Sync — must work in user-gesture flow for Apple Pay.
  const validateBeforeSquare = () => canCheckoutSync();

  // Cash App reservation flow. Customer reviewed the QR + handle + total,
  // sent payment from their Cash App app, and clicked "I've sent payment".
  // We create a reservation order; admin confirms once funds arrive.
  const handleConfirmCashAppSent = async () => {
    if (!canCheckoutSync()) return;
    if (cashAppSubmitting) return;
    setCashAppSubmitting(true);
    setReserveError(null);
    try {
      // FINAL stock re-check right before we record the claim. A line that
      // sold out (or whose variant changed) while the customer was on the
      // page would otherwise 400 the reservation AFTER they sent money — the
      // exact "we don't receive the order" bug. Catch it here, loudly.
      const freshIssues = await revalidateStock();
      if (freshIssues.length > 0) {
        setShowCashApp(false);
        setReserveError("Your reservation was NOT placed — one or more items in your cart are no longer available (see the highlighted items above). Please remove them and try again. If you already sent a Cash App payment, do NOT send more — email research@genorapeptides.com and we'll help right away.");
        toast.error('Reservation not placed — a cart item is out of stock.');
        return;
      }
      const payload = {
        ...getCheckoutPayload(),
        payment_method: 'cash_app',
        notes: (cashAppOrderId ? `Customer reserved via Cash App (awaiting payment). Local ref: ${cashAppOrderId}` : 'Customer reserved via Cash App (awaiting payment).'),
        contact: {
          name: user?.name || shippingAddress.name || '',
          email: user?.email || '',
          phone: phone.trim(),
        },
      };
      const res = await fetch(`${API_URL}/api/orders/reserve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        if (res.status === 401) {
          setReserveError("Your session expired before we could place your order, so your reservation was NOT created. Your cart is saved — please sign in again, then retry. Do NOT send a Cash App payment until you see an order confirmation.");
          toast.error('Session expired — please sign in again. Your reservation was not placed.');
          return;
        }
        const msg = typeof data?.detail === 'string' ? data.detail : 'Could not record your Cash App reservation. Please try again.';
        setReserveError(`Your reservation was NOT placed: ${msg} Please fix this and try again. If you already sent a Cash App payment, email research@genorapeptides.com so we can match it to your order.`);
        toast.error(msg);
        return;
      }
      const data = await res.json();
      setReserveError(null);
      toast.success(`Reservation confirmed! Check your email for payment instructions and your total — your order is held for ${siteSettings?.auto_cancel_stale_reservations_hours || 48} hours.`);
      clearCart();
      navigate(`/checkout/success?free_order=${data.order_id}`);
    } catch (e) {
      console.error('Cash App reserve error:', e);
      setReserveError("We couldn't reach the server to place your reservation, so it was NOT created. Your cart is saved. Please check your connection and try again — do NOT send a Cash App payment until you see an order confirmation.");
      toast.error('Could not record your Cash App payment. Please try again.');
    } finally {
      setCashAppSubmitting(false);
    }
  };

  // Zelle reservation flow. Mirrors Cash App: customer reviewed the QR +
  // email + total, sent payment from their bank's Zelle, and clicked
  // "I've sent payment". Admin confirms once the deposit appears.
  const handleConfirmZelleSent = async () => {
    if (!canCheckoutSync()) return;
    if (zelleSubmitting) return;
    const zName = zelleSenderName.trim();
    const zContact = zelleSenderContact.trim();
    if (!zName || !zContact) {
      setReserveError("Please enter the name and the phone number or email associated with your Zelle account so we can send you a Zelle request for the exact amount.");
      toast.error('Please add your Zelle name and phone/email.');
      return;
    }
    setZelleSubmitting(true);
    setReserveError(null);
    try {
      // FINAL stock re-check right before recording the claim (see Cash App).
      const freshIssues = await revalidateStock();
      if (freshIssues.length > 0) {
        setShowZelle(false);
        setReserveError("Your reservation was NOT placed — one or more items in your cart are no longer available (see the highlighted items above). Please remove them and try again. If you already sent a Zelle payment, do NOT send more — email research@genorapeptides.com and we'll help right away.");
        toast.error('Reservation not placed — a cart item is out of stock.');
        return;
      }
      const payload = {
        ...getCheckoutPayload(),
        payment_method: 'zelle',
        notes: (zelleOrderId ? `Customer reserved via Zelle (awaiting payment). Local ref: ${zelleOrderId}` : 'Customer reserved via Zelle (awaiting payment).'),
        zelle_contact: { name: zName, handle: zContact },
        contact: {
          name: user?.name || shippingAddress.name || '',
          email: user?.email || '',
          phone: phone.trim(),
        },
      };
      const res = await fetch(`${API_URL}/api/orders/reserve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        if (res.status === 401) {
          setReserveError("Your session expired before we could place your order, so your reservation was NOT created. Your cart is saved — please sign in again, then retry. Do NOT send a Zelle payment until you see an order confirmation.");
          toast.error('Session expired — please sign in again. Your reservation was not placed.');
          return;
        }
        const msg = typeof data?.detail === 'string' ? data.detail : 'Could not record your Zelle reservation. Please try again.';
        setReserveError(`Your reservation was NOT placed: ${msg} Please fix this and try again. If you already sent a Zelle payment, email research@genorapeptides.com so we can match it to your order.`);
        toast.error(msg);
        return;
      }
      const data = await res.json();
      setReserveError(null);
      toast.success(`Reservation confirmed! Check your email for payment instructions and your total — your order is held for ${siteSettings?.auto_cancel_stale_reservations_hours || 48} hours.`);
      clearCart();
      navigate(`/checkout/success?free_order=${data.order_id}`);
    } catch (e) {
      console.error('Zelle reserve error:', e);
      setReserveError("We couldn't reach the server to place your reservation, so it was NOT created. Your cart is saved. Please check your connection and try again — do NOT send a Zelle payment until you see an order confirmation.");
      toast.error('Could not record your Zelle payment. Please try again.');
    } finally {
      setZelleSubmitting(false);
    }
  };

  // Called by <SquareCardForm> after a card is tokenized. We POST the token
  // + cart payload to /api/square/process-payment and let the server charge.
  const handleSquarePaymentToken = async (squareToken, verificationToken) => {
    setLoading(true);
    try {
      const payload = {
        ...getCheckoutPayload(),
        source_id: squareToken,
        verification_token: verificationToken || null,
      };
      const response = await fetch(`${API_URL}/api/square/process-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        if (response.status === 409 && errorData?.detail?.stock_issues) {
          handleStockError(errorData.detail);
          return;
        }
        const msg = typeof errorData?.detail === 'string'
          ? errorData.detail
          : 'Payment failed. Please try again or contact support.';
        toast.error(msg);
        return;
      }
      const data = await response.json();
      toast.success('Payment successful!');
      clearCart();
      navigate(`/checkout/success?free_order=${data.order_id}`);
    } catch (error) {
      console.error('Square checkout error:', error);
      toast.error('Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50">
        <SEO 
          title="Shopping Cart"
          description="Your Genora Peptides shopping cart. Review your research peptide selections before checkout."
          keywords="shopping cart, peptide order, research peptides"
        />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <div className="text-center">
            <ShoppingCart className="h-16 w-16 text-slate-300 mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-4">Your cart is empty</h1>
            <p className="text-slate-600 mb-8">Browse our research peptides and add items to your cart.</p>
            <Link to="/products">
              <Button className="bg-teal-700 hover:bg-teal-800 text-white">
                Browse Products
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO 
        title="Shopping Cart"
        description="Your Genora Peptides shopping cart. Review your research peptide selections before checkout."
        keywords="shopping cart, peptide order, research peptides"
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        <Link 
          to="/products" 
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-teal-700 mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Continue Shopping
        </Link>

        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-8" data-testid="cart-title">
          Shopping Cart ({itemCount} {itemCount === 1 ? 'item' : 'items'})
        </h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {items.some(item => item.is_preorder) && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="cart-preorder-disclaimer">
                <p className="font-medium text-amber-800">Pre-Order Items</p>
                <p className="text-sm text-amber-600 mt-1">
                  Longer shipping times may apply due to availability. Usually 2 weeks.
                  In-stock items in this order will ship now; pre-order items will follow when ready.
                </p>
              </div>
            )}
            {/* Cart hold-time banner — explains the 15-min countdown on each line */}
            {cartCountdownEnabled && (
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg flex items-center gap-3" data-testid="cart-hold-banner">
              <Clock className="h-4 w-4 text-slate-500 flex-shrink-0" />
              <p className="text-xs text-slate-600">
                Items are held in your cart for 15 minutes so other shoppers can buy them too.
                Updating quantities resets the timer.
              </p>
            </div>
            )}
            {stockIssues.length > 0 && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="stock-issues-banner">
                <p className="font-medium text-red-800 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Stock Changes Detected
                </p>
                <p className="text-sm text-red-600 mt-1">
                  Some items in your cart are no longer available at the requested quantity. 
                  Please remove them or switch to pre-order before checking out.
                </p>
              </div>
            )}
            {items.filter(item => item && item.name && item.price && !isNaN(item.price)).map((item) => (
              <Card key={`${item.product_id}-${item.vial_size}`} className="bg-white border-slate-100" data-testid={`cart-item-${item.product_id}`}>
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    {/* Image */}
                    <div className="w-24 h-24 bg-slate-50 rounded-lg flex-shrink-0">
                      <img 
                        src={item.image_url?.startsWith('/api') ? `${API_URL}${item.image_url}` : (item.image_url || '/placeholder.png')} 
                        alt={item.name || 'Product'}
                        className="w-full h-full object-contain"
                      />
                    </div>

                    {/* Details */}
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <div>
                          <h3 className="font-heading font-semibold text-slate-900">{item.name}</h3>
                          <p className="text-sm text-slate-500">{item.vial_size}</p>
                          {item.is_preorder && (
                            <Badge className="mt-1 bg-amber-100 text-amber-700 border-amber-200 text-xs" data-testid={`preorder-badge-${item.product_id}`}>
                              Pre-Order
                            </Badge>
                          )}
                          {item.item_type === 'shop' && !item.discount_eligible && (
                            <Badge className="mt-1 bg-red-50 text-red-600 border-red-200 text-xs" data-testid={`no-discount-cart-${item.product_id}`}>
                              Not Eligible for Discount
                            </Badge>
                          )}
                          {item.item_type !== 'shop' && !isItemDiscountEligible(item) && (
                            <Badge className="mt-1 bg-red-50 text-red-600 border-red-200 text-xs" data-testid={`no-member-discount-${item.product_id}`}>
                              Not eligible for member discount
                            </Badge>
                          )}
                          {item.item_type === 'shop' && (
                            <Badge className="mt-1 bg-teal-50 text-teal-700 border-teal-200 text-xs">
                              Shop
                            </Badge>
                          )}
                          {/* Cart-item TTL countdown — items auto-expire after 15 min so they
                              don't artificially block other shoppers. Resets on quantity changes.
                              Only rendered when the admin has enabled the cart countdown. */}
                          {cartCountdownEnabled && item.added_at && (() => {
                            const TTL = 15 * 60 * 1000;
                            const elapsed = now - item.added_at;
                            const remaining = Math.max(0, TTL - elapsed);
                            const mins = Math.floor(remaining / 60000);
                            const secs = Math.floor((remaining % 60000) / 1000);
                            const tone = remaining > 5 * 60 * 1000
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : remaining > 60 * 1000
                                ? 'bg-amber-50 text-amber-700 border-amber-200'
                                : 'bg-rose-50 text-rose-700 border-rose-200';
                            return (
                              <Badge
                                className={`mt-1 ml-1 ${tone} text-xs inline-flex items-center gap-1`}
                                title="Items in your cart are held for 15 minutes so other shoppers can buy them too. Updating the quantity resets the timer."
                                data-testid={`cart-item-timer-${item.product_id}`}
                              >
                                <Clock className="h-3 w-3" />
                                {mins}m {String(secs).padStart(2, '0')}s left
                              </Badge>
                            );
                          })()}
                          {!item.is_preorder && typeof item.stock_quantity === 'number' && item.stock_quantity > 0 && (
                            <p className="text-xs text-slate-400 mt-1" data-testid={`stock-info-${item.product_id}`}>
                              {item.stock_quantity} in stock
                            </p>
                          )}
                          {/* Stock issue warning from validation */}
                          {stockIssues.find(si => si.product_id === item.product_id && si.vial_size === item.vial_size) && (
                            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md" data-testid={`stock-issue-${item.product_id}`}>
                              <p className="text-xs text-red-700 font-semibold flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />
                                {stockIssues.find(si => si.product_id === item.product_id && si.vial_size === item.vial_size)?.status === 'out_of_stock'
                                  ? 'This item is no longer in stock.'
                                  : `Only ${stockIssues.find(si => si.product_id === item.product_id && si.vial_size === item.vial_size)?.available} available.`
                                }
                              </p>
                              <p className="text-xs text-red-600 mt-1">
                                Remove this item or{' '}
                                <Link to={`/products/${item.slug || ''}`} className="underline font-medium">switch to pre-order</Link>.
                              </p>
                            </div>
                          )}
                        </div>
                        <button 
                          onClick={() => removeItem(item.product_id, item.vial_size)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                          data-testid={`remove-${item.product_id}`}
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>

                      <div className="flex items-center justify-between mt-4">
                        {/* Quantity */}
                        <div className="flex items-center gap-3 bg-slate-50 rounded-lg px-3 py-1">
                          <button 
                            onClick={() => updateQuantity(item.product_id, item.quantity - 1, item.vial_size)}
                            className="text-slate-500 hover:text-slate-700"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="font-medium w-6 text-center">{item.quantity}</span>
                          <button 
                            onClick={() => updateQuantity(item.product_id, item.quantity + 1, item.vial_size)}
                            className={`${!item.is_preorder && typeof item.stock_quantity === 'number' && item.quantity >= item.stock_quantity ? 'text-slate-300 cursor-not-allowed' : 'text-slate-500 hover:text-slate-700'}`}
                            disabled={!item.is_preorder && typeof item.stock_quantity === 'number' && item.quantity >= item.stock_quantity}
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Price */}
                        <div className="flex flex-col items-end">
                          <span className="font-heading font-bold text-teal-700">
                            ${((item.price || 0) * item.quantity).toFixed(2)}
                          </span>
                          {item.is_on_sale && item.original_price && (
                            <span className="text-xs text-slate-400 line-through" data-testid={`cart-item-original-price-${item.product_id}`}>
                              ${(item.original_price * item.quantity).toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Stock limit warning with pre-order option */}
                      {!item.is_preorder && typeof item.stock_quantity === 'number' && item.stock_quantity > 0 && item.quantity >= item.stock_quantity && (
                        <div className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg" data-testid={`stock-limit-warning-${item.product_id}`}>
                          <p className="text-xs text-amber-700">
                            <strong>Max stock reached.</strong> Need more? You can pre-order additional units — they'll ship when available.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            <button 
              onClick={clearCart}
              className="text-sm text-slate-500 hover:text-red-500"
              data-testid="clear-cart"
            >
              Clear cart
            </button>
          </div>

          {/* Order Summary */}
          <div>
            {reminder && (
              <Card className="mb-4 bg-amber-50 border-amber-200" data-testid="cart-supplies-reminder">
                <CardContent className="p-4">
                  <div className="flex items-start gap-2 mb-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-heading font-semibold text-amber-900 text-sm">Don't forget your supplies</h3>
                      <p className="text-xs text-amber-700 mt-0.5">
                        Research compounds need to be reconstituted and dosed safely. A quick add now saves a separate order later.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {reminder.map(p => {
                      const price = Number(p.base_price ?? p.price ?? 0);
                      const inStock = (p.stock_quantity ?? 0) > 0
                        || (p.pack_sizes || []).some(ps => (ps.stock_quantity ?? 0) > 0)
                        || (p.colors || []).some(c => (c.stock_quantity ?? 0) > 0);
                      return (
                        <div key={p.product_id} className="flex items-center justify-between bg-white rounded-md p-2 border border-amber-100" data-testid={`reminder-item-${p.product_id}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            {p.image_url ? (
                              <img
                                src={p.image_url.startsWith('/api') ? `${API_URL}${p.image_url}` : p.image_url}
                                alt={p.name}
                                className="w-10 h-10 object-contain bg-slate-50 rounded flex-shrink-0"
                              />
                            ) : (
                              <div className="w-10 h-10 bg-slate-100 rounded flex-shrink-0" />
                            )}
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">{p.name}</p>
                              <p className="text-xs text-slate-500">${price.toFixed(2)}</p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!inStock}
                            onClick={() => handleQuickAddSupply(p)}
                            className="border-amber-300 text-amber-800 hover:bg-amber-100 flex-shrink-0"
                            data-testid={`quick-add-${p.product_id}`}
                          >
                            <Plus className="h-3.5 w-3.5 mr-1" />
                            {inStock ? 'Add' : 'Out'}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
            <Card className="bg-white border-slate-100 sticky top-24">
              <CardContent className="p-6">
                <h2 className="font-heading font-semibold text-slate-900 mb-6">Order Summary</h2>

                {/* Wholesale Tier Notice */}
                {isWholesale && (
                  <div className={`mb-4 p-4 rounded-lg border ${meetsMinimum ? 'bg-teal-50 border-teal-200' : 'bg-amber-50 border-amber-200'}`}>
                    <div className="flex items-start gap-3">
                      <Package className={`h-5 w-5 mt-0.5 ${meetsMinimum ? 'text-teal-600' : 'text-amber-600'}`} />
                      <div>
                        <p className={`text-sm font-semibold ${meetsMinimum ? 'text-teal-800' : 'text-amber-800'}`}>
                          Wholesale Order
                        </p>
                        <p className={`text-sm ${meetsMinimum ? 'text-teal-700' : 'text-amber-700'}`}>
                          {meetsMinimum 
                            ? `✓ Minimum ${minQuantity} items met (${totalItems} in cart)`
                            : `Minimum ${minQuantity} items required (${totalItems} in cart)`
                          }
                        </p>
                        {leadTimeDays && meetsMinimum && (
                          <div className="flex items-center gap-1 mt-2 text-sm text-teal-600">
                            <Clock className="h-4 w-4" />
                            <span>Orders typically ship within {leadTimeDays === 14 ? '2 weeks' : `${leadTimeDays} days`}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Pricing Tier Badge */}
                {pricingInfo?.has_special_pricing && pricingInfo?.tier && (
                  <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-purple-800">{pricingInfo.tier.name}</span>
                      <Badge className="bg-purple-600 text-white">{pricingInfo.tier.discount_value}% OFF</Badge>
                    </div>
                  </div>
                )}

                {/* Discount Code Input */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Discount Code
                  </label>
                  {appliedDiscount?.source === 'code' ? (
                    <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Tag className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium text-green-700">{appliedDiscount.code}</span>
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          {appliedDiscount.type === 'percentage' ? `${appliedDiscount.value}% off` : `$${appliedDiscount.value} off`}
                        </Badge>
                      </div>
                      <button
                        onClick={handleRemoveDiscount}
                        className="p-1 text-slate-400 hover:text-red-500"
                        title="Remove code"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter code"
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                        className="flex-1"
                        data-testid="discount-code-input"
                      />
                      <Button
                        variant="outline"
                        onClick={handleApplyDiscountCode}
                        disabled={applyingCode || !discountCode.trim()}
                        data-testid="apply-discount-btn"
                      >
                        {applyingCode ? '...' : 'Apply'}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Tier Discount Badge */}
                {appliedDiscount?.source === 'tier' && (
                  <div className="flex items-center gap-2 p-3 bg-purple-50 border border-purple-200 rounded-lg mb-4">
                    <Percent className="h-4 w-4 text-purple-600" />
                    <span className="text-sm text-purple-700">
                      <strong>{appliedDiscount.name}</strong> tier discount applied
                    </span>
                    <Badge className="bg-purple-100 text-purple-700 text-xs ml-auto">
                      {appliedDiscount.type === 'percentage' ? `${appliedDiscount.value}% off` : `$${appliedDiscount.value} off`}
                    </Badge>
                  </div>
                )}

                {/* Delivery Options */}
                {(deliveryOptions?.local_pickup?.available || deliveryOptions?.cash_payment?.available) && (
                  <div className="mb-4 p-4 bg-teal-50 border border-teal-200 rounded-lg">
                    <p className="text-sm font-medium text-teal-800 mb-3">Delivery Method</p>
                    <div className="space-y-2">
                      <label className="flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-teal-100">
                        <input
                          type="radio"
                          name="delivery"
                          value="shipping"
                          checked={deliveryMethod === 'shipping'}
                          onChange={(e) => setDeliveryMethod(e.target.value)}
                          className="text-teal-600"
                        />
                        <Truck className="h-4 w-4 text-teal-600" />
                        <div className="flex-1">
                          <span className="text-sm font-medium">Standard Shipping</span>
                          <p className="text-xs text-slate-500">2-4 business days</p>
                        </div>
                        <span className="text-sm font-semibold">$10.00</span>
                      </label>
                      {deliveryOptions?.local_pickup?.available && (
                        <label className="flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-teal-100">
                          <input
                            type="radio"
                            name="delivery"
                            value="local_pickup"
                            checked={deliveryMethod === 'local_pickup'}
                            onChange={(e) => setDeliveryMethod(e.target.value)}
                            className="text-teal-600"
                          />
                          <MapPin className="h-4 w-4 text-teal-600" />
                          <div className="flex-1">
                            <span className="text-sm font-medium">Local Pickup</span>
                            <p className="text-xs text-slate-500">Request your pickup date</p>
                          </div>
                          <span className="text-sm font-semibold text-green-600">FREE</span>
                        </label>
                      )}
                      {deliveryOptions?.cash_payment?.available && (
                        <label className="flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-amber-50" data-testid="cash-payment-option">
                          <input
                            type="radio"
                            name="delivery"
                            value="cash_payment"
                            checked={deliveryMethod === 'cash_payment'}
                            onChange={(e) => setDeliveryMethod(e.target.value)}
                            className="text-amber-600"
                          />
                          <DollarSign className="h-4 w-4 text-amber-600" />
                          <div className="flex-1">
                            <span className="text-sm font-medium">Pay with Cash / In-Person</span>
                            <p className="text-xs text-slate-500">Pay when you pick up your order</p>
                          </div>
                          <span className="text-sm font-semibold text-green-600">FREE shipping</span>
                        </label>
                      )}
                    </div>
                    
                    {(deliveryMethod === 'local_pickup' || deliveryMethod === 'cash_payment') && (
                      <div className="mt-3 pt-3 border-t border-teal-200 space-y-3">
                        {deliveryMethod === 'local_pickup' && (
                          <>
                            <div>
                              <label className="block text-sm font-medium text-teal-800 mb-2">
                                <Calendar className="h-4 w-4 inline mr-1" />
                                Select Pickup Date
                              </label>
                              {loadingDates ? (
                                <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                                  <Clock className="h-4 w-4 animate-spin" /> Loading available dates...
                                </div>
                              ) : availableDates.length > 0 ? (
                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                                  {availableDates.slice(0, 20).map((d) => {
                                    const dateObj = new Date(d + 'T12:00:00');
                                    const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
                                    const monthDay = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                    return (
                                      <button
                                        key={d}
                                        type="button"
                                        data-testid={`pickup-date-${d}`}
                                        onClick={() => setPickupDate(d)}
                                        className={`p-2 rounded-lg text-center text-sm border transition-all ${
                                          pickupDate === d
                                            ? 'bg-teal-600 text-white border-teal-600 ring-2 ring-teal-300'
                                            : 'bg-white hover:bg-teal-50 border-slate-200 hover:border-teal-400'
                                        }`}
                                      >
                                        <div className="font-medium">{dayName}</div>
                                        <div className="text-xs opacity-80">{monthDay}</div>
                                      </button>
                                    );
                                  })}
                                </div>
                              ) : (
                                <p className="text-sm text-amber-600">No dates available. Please try again later.</p>
                              )}
                            </div>

                            {pickupDate && (
                              <div>
                                <label className="block text-sm font-medium text-teal-800 mb-2">
                                  <Clock className="h-4 w-4 inline mr-1" />
                                  Select Time Slot
                                </label>
                                {loadingSlots ? (
                                  <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                                    <Clock className="h-4 w-4 animate-spin" /> Loading time slots...
                                  </div>
                                ) : availableSlots.length > 0 ? (
                                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    {availableSlots.map((slot) => (
                                      <button
                                        key={slot.start}
                                        type="button"
                                        data-testid={`pickup-slot-${slot.start}`}
                                        onClick={() => setPickupTime(slot.start)}
                                        className={`p-2 rounded-lg text-sm border transition-all ${
                                          pickupTime === slot.start
                                            ? 'bg-teal-600 text-white border-teal-600 ring-2 ring-teal-300'
                                            : 'bg-white hover:bg-teal-50 border-slate-200 hover:border-teal-400'
                                        }`}
                                      >
                                        {slot.label}
                                      </button>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-amber-600">No time slots available for this date.</p>
                                )}
                              </div>
                            )}
                          </>
                        )}

                        {deliveryMethod === 'cash_payment' && (
                          <div>
                            <label className="block text-sm font-medium text-teal-800 mb-2">
                              <Calendar className="h-4 w-4 inline mr-1" />
                              Preferred Pickup Date
                            </label>
                            <Input
                              type="date"
                              value={pickupDate}
                              onChange={(e) => setPickupDate(e.target.value)}
                              min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                              className="bg-white"
                              required
                            />
                          </div>
                        )}

                        {pickupDate && (
                          <p className="text-xs text-teal-600">
                            Selected: {new Date(pickupDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                            {pickupTime && ` at ${availableSlots.find(s => s.start === pickupTime)?.label || pickupTime}`}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Subtotal ({totalItems} items)</span>
                    <span className="font-medium">${total.toFixed(2)}</span>
                  </div>
                  {discountAmount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>
                        Discount {appliedDiscount?.source === 'tier' ? `(${appliedDiscount.name})` : `(${appliedDiscount?.code})`}
                      </span>
                      <span className="font-medium">-${discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">
                      {deliveryMethod === 'local_pickup' ? 'Local Pickup' : deliveryMethod === 'cash_payment' ? 'Cash / In-Person' : 'Shipping (2-4 business days)'}
                    </span>
                    <span className={`font-medium ${shippingCost === 0 ? 'text-green-600' : ''}`} data-testid="cart-shipping-amount">
                      {shippingCost === 0 ? 'FREE' : '$10.00'}
                    </span>
                  </div>
                  {deliveryMethod === 'shipping' && freeShipThreshold > 0 && !qualifiesFreeShipping && (
                    <p className="text-xs text-teal-700 -mt-1" data-testid="free-shipping-hint">
                      Add ${(freeShipThreshold - total).toFixed(2)} more to get FREE shipping.
                    </p>
                  )}
                  {deliveryMethod === 'shipping' && (siteSettings?.shipping_cutoff_enabled ?? true) && (
                    <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5 -mt-1" data-testid="shipping-cutoff-notice">
                      <Clock className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                      <p className="text-xs text-amber-800">
                        Orders placed after <strong>{siteSettings?.shipping_cutoff_time || '3:00 PM PT'}</strong> will be sent the following day.
                      </p>
                    </div>
                  )}
                  {user?.store_credit > 0 && (
                    <div className="mt-2 p-3 bg-green-50 border border-green-100 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-green-700 flex items-center gap-1.5 font-medium">
                          <Award className="h-4 w-4" /> Store Credit Available: ${user.store_credit.toFixed(2)}
                        </span>
                        <button
                          type="button"
                          onClick={() => { setUseCredit(!useCredit); if (!useCredit) setCreditAmount(null); }}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${useCredit ? 'bg-green-500' : 'bg-slate-300'}`}
                          data-testid="credit-toggle"
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${useCredit ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </button>
                      </div>
                      {useCredit && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-green-600">Apply:</span>
                          <div className="flex items-center gap-1 flex-1">
                            <span className="text-xs text-green-600">$</span>
                            <Input
                              type="number"
                              min="0"
                              max={Math.min(user.store_credit, finalTotal)}
                              step="0.01"
                              value={creditAmount !== null ? creditAmount : Math.min(user.store_credit, finalTotal).toFixed(2)}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                const maxCredit = Math.min(user.store_credit, finalTotal);
                                setCreditAmount(Math.min(Math.max(val, 0), maxCredit));
                              }}
                              className="h-7 text-xs text-right w-24"
                              data-testid="credit-amount-input"
                            />
                            <span className="text-xs text-green-600">of ${user.store_credit.toFixed(2)}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setCreditAmount(null)}
                            className="text-xs text-green-600 hover:text-green-800 underline whitespace-nowrap"
                          >
                            Use max
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="border-t border-slate-100 pt-4 mb-6">
                  <div className="flex justify-between">
                    <span className="font-heading font-semibold">Total</span>
                    <span className="font-heading font-bold text-xl text-teal-700" data-testid="cart-total">
                      ${(() => {
                        let t = finalTotal;
                        if (user?.store_credit > 0 && useCredit) {
                          const applied = creditAmount !== null ? creditAmount : Math.min(user.store_credit, finalTotal);
                          t = Math.max(0, t - applied);
                        }
                        return t.toFixed(2);
                      })()}
                    </span>
                  </div>
                  {user?.store_credit > 0 && useCredit && (
                    <p className="text-xs text-green-600 text-right mt-1">
                      ${(creditAmount !== null ? creditAmount : Math.min(user.store_credit, finalTotal)).toFixed(2)} store credit applied
                    </p>
                  )}
                  {discountAmount > 0 && (
                    <p className="text-xs text-green-600 text-right mt-1">
                      You save ${discountAmount.toFixed(2)}!
                    </p>
                  )}
                </div>

                {/* Contact + Address — required on every order so the team
                    can reach the customer and we have a record for every
                    sale. Even pickup orders capture this for compliance. */}
                <div className="mb-4 p-4 bg-white rounded-lg border border-slate-200" data-testid="shipping-address-form">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
                      <Truck className="h-4 w-4 text-teal-700" />
                      {deliveryMethod === 'shipping' ? 'Shipping Address & Phone' : 'Contact & Address'}
                    </h3>
                    {savedAddresses.length > 0 && (
                      <select
                        className="text-xs border border-slate-300 rounded px-2 py-1"
                        data-testid="saved-address-select"
                        onChange={(e) => {
                          const id = e.target.value;
                          if (!id) return;
                          const a = savedAddresses.find(x => x.address_id === id);
                          if (a) {
                            setShippingAddress({
                              name: user?.name || shippingAddress.name || '',
                              address_line1: a.street || '',
                              address_line2: a.street2 || '',
                              city: a.city || '',
                              state: a.state || '',
                              zip_code: a.zip_code || '',
                              country: a.country === 'United States' ? 'US' : (a.country || 'US'),
                            });
                          }
                        }}
                        defaultValue=""
                      >
                        <option value="">Use saved address...</option>
                        {savedAddresses.map(a => (
                          <option key={a.address_id} value={a.address_id}>
                            {a.label || 'Address'} — {a.street}, {a.city}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input
                      placeholder="Full Name *"
                      value={shippingAddress.name}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, name: e.target.value })}
                      data-testid="ship-name"
                    />
                    <Input
                      placeholder="Phone Number * (e.g. 555-123-4567)"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      type="tel"
                      data-testid="ship-phone"
                    />
                    <Input
                      placeholder="Street Address *"
                      value={shippingAddress.address_line1}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, address_line1: e.target.value })}
                      data-testid="ship-line1"
                      className="sm:col-span-2"
                    />
                    <Input
                      placeholder="Apt, Suite, etc. (optional)"
                      value={shippingAddress.address_line2}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, address_line2: e.target.value })}
                      data-testid="ship-line2"
                      className="sm:col-span-2"
                    />
                    <Input
                      placeholder="City *"
                      value={shippingAddress.city}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                      data-testid="ship-city"
                    />
                    <Input
                      placeholder="State *"
                      value={shippingAddress.state}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, state: e.target.value.toUpperCase().slice(0, 2) })}
                      maxLength={2}
                      data-testid="ship-state"
                    />
                    <Input
                      placeholder="ZIP Code *"
                      value={shippingAddress.zip_code}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, zip_code: e.target.value })}
                      data-testid="ship-zip"
                    />
                    <Input
                      placeholder="Country"
                      value={shippingAddress.country}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, country: e.target.value.toUpperCase().slice(0, 2) })}
                      maxLength={2}
                      data-testid="ship-country"
                    />
                  </div>
                  {(!shippingAddressValid || !phoneValid) && (
                    <p className="text-xs text-amber-700 mt-2">
                      Required on every order. Phone is how we reach you about your shipment, payment, and any issues.
                    </p>
                  )}
                </div>

                {/* Research Consent Checkbox */}
                <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div 
                    className="flex items-start gap-3 cursor-pointer group"
                    onClick={() => setResearchConsent(!researchConsent)}
                    role="checkbox"
                    aria-checked={researchConsent}
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setResearchConsent(!researchConsent); }}}
                    data-testid="research-consent-label"
                  >
                    <div
                      className={`flex-shrink-0 p-1 min-w-[32px] min-h-[32px] flex items-center justify-center rounded transition-colors ${researchConsent ? 'text-teal-600' : 'text-slate-400 group-hover:text-slate-600'}`}
                      data-testid="research-consent-checkbox"
                    >
                      {researchConsent ? (
                        <CheckSquare className="h-6 w-6" />
                      ) : (
                        <Square className="h-6 w-6" />
                      )}
                    </div>
                    <span className="text-sm text-slate-700 leading-snug select-none">
                      I confirm that I am purchasing these products for <strong>research purposes only</strong>. 
                      I understand that these products are not intended for human consumption, and I agree to 
                      use them in accordance with all applicable laws and regulations.
                    </span>
                  </div>
                </div>

                {/* Wholesale minimum warning */}
                {isWholesale && !meetsMinimum && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2 text-amber-700">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">
                        Add {minQuantity - totalItems} more items to meet wholesale minimum
                      </span>
                    </div>
                  </div>
                )}

                {user ? (
                  <div data-testid="checkout-section">
                    {/* Persistent reservation-failure banner — stays visible
                        (unlike a toast) so a customer can never miss that their
                        Cash App / Zelle order did NOT go through. */}
                    {reserveError && (
                      <div
                        className="mb-4 rounded-lg border-2 border-red-400 bg-red-50 p-4 flex items-start gap-3"
                        role="alert"
                        data-testid="reserve-error-banner"
                      >
                        <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="space-y-2">
                          <p className="text-sm font-semibold text-red-800">Order not placed</p>
                          <p className="text-sm text-red-700">{reserveError}</p>
                          <button
                            type="button"
                            onClick={() => setReserveError(null)}
                            className="text-xs font-medium text-red-700 underline hover:text-red-900"
                            data-testid="reserve-error-dismiss"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    )}
                    {/* Show validation errors or loading states */}
                    {stockValidating && (
                      <Button className="w-full h-12 bg-slate-400 text-white cursor-not-allowed" disabled>
                        <span className="flex items-center gap-2">
                          <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                          Checking stock...
                        </span>
                      </Button>
                    )}
                    {!stockValidating && stockIssues.length > 0 && (
                      <Button className="w-full h-12 bg-red-400 text-white cursor-not-allowed" disabled data-testid="checkout-button-blocked">
                        <AlertCircle className="mr-2 h-4 w-4" />
                        Fix Stock Issues to Checkout
                      </Button>
                    )}
                    {!stockValidating && stockIssues.length === 0 && !researchConsent && (
                      <Button className="w-full h-12 bg-slate-400 text-white cursor-not-allowed" disabled>
                        Confirm Research Consent Above
                      </Button>
                    )}
                    {!stockValidating && stockIssues.length === 0 && isWholesale && !meetsMinimum && (
                      <Button className="w-full h-12 bg-slate-400 text-white cursor-not-allowed" disabled>
                        Minimum {minQuantity} Items Required
                      </Button>
                    )}
                    {/* Cash payment button (no PayPal needed) */}
                    {!stockValidating && stockIssues.length === 0 && researchConsent && (!isWholesale || meetsMinimum) && deliveryMethod === 'cash_payment' && (
                      <Button 
                        className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white"
                        onClick={handleCheckout}
                        disabled={loading}
                        data-testid="checkout-button"
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                            Processing...
                          </span>
                        ) : (
                          <>
                            <DollarSign className="mr-2 h-4 w-4" />
                            Place Cash Order
                          </>
                        )}
                      </Button>
                    )}
                    {/* Payment Method Selector + Buttons */}
                    {!stockValidating && stockIssues.length === 0 && researchConsent && (!isWholesale || meetsMinimum) && deliveryMethod !== 'cash_payment' && (
                      <div data-testid="payment-method-section">

                        {loading && (
                          <div className="flex items-center justify-center gap-2 py-3 text-slate-500">
                            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-700"></span>
                            Processing payment...
                          </div>
                        )}

                        {/* Square Web Payments form OR "payments disabled" banner.
                            When the admin has flipped online payments off,
                            we surface a polite notice and let Cash App /
                            Zelle (below) handle the order. */}
                        {!onlinePaymentsEnabled ? (
                          <div
                            className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-center space-y-2"
                            data-testid="payments-disabled-banner"
                          >
                            <p className="text-sm font-semibold text-amber-900">
                              {paymentsDisabledMessage}
                            </p>
                            <p className="text-xs text-amber-800">
                              Please pay with Cash App or Zelle below to reserve your order.
                            </p>
                            {contactEmail && (
                              <a
                                href={`mailto:${contactEmail}?subject=Order%20inquiry`}
                                className="inline-block text-sm font-medium text-teal-700 hover:underline"
                                data-testid="payments-disabled-contact"
                              >
                                Or email us: {contactEmail}
                              </a>
                            )}
                          </div>
                        ) : (
                          <div data-testid="square-payment-container">
                            <SquareCardForm
                              applicationId={SQUARE_APP_ID}
                              locationId={SQUARE_LOCATION_ID}
                              environment={SQUARE_ENV}
                              amount={(() => {
                                let t = finalTotal;
                                if (user?.store_credit > 0 && useCredit) {
                                  const applied = creditAmount !== null
                                    ? creditAmount
                                    : Math.min(user.store_credit, finalTotal);
                                  t = Math.max(0, t - applied);
                                }
                                return t;
                              })()}
                              buyerEmail={user?.email}
                              disabled={loading}
                              onBeforeTokenize={validateBeforeSquare}
                              onPaymentToken={handleSquarePaymentToken}
                            />
                          </div>
                        )}

                        {/* Cash App stopgap option. Always available when
                            admin has flipped cashapp_enabled on, in parallel
                            to Square. */}
                        {cashappEnabled && (
                          <div className="mt-4" data-testid="cashapp-payment-section">
                            {!showCashApp ? (
                              <>
                                <div className="flex items-center gap-3 my-3">
                                  <div className="flex-1 h-px bg-slate-200" />
                                  <span className="text-xs uppercase tracking-wide text-slate-400">or</span>
                                  <div className="flex-1 h-px bg-slate-200" />
                                </div>
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => {
                                    if (!canCheckoutSync()) return;
                                    setCashAppOrderId(`CA-${Date.now().toString(36).toUpperCase().slice(-6)}`);
                                    setShowCashApp(true);
                                  }}
                                  className="w-full h-14 border-2 border-green-500 text-green-700 hover:bg-green-50 font-semibold flex flex-col items-center justify-center gap-0.5"
                                  data-testid="open-cashapp-pay"
                                >
                                  <span className="flex items-center gap-2">
                                    <DollarSign className="h-4 w-4" />
                                    Pay with Cash App
                                  </span>
                                </Button>
                              </>
                            ) : (
                              <div
                                className="rounded-xl border-2 border-green-500 bg-white p-4 space-y-3"
                                data-testid="cashapp-pay-panel"
                              >
                                <div className="flex items-center justify-between">
                                  <h3 className="font-heading font-semibold text-slate-900">Pay with Cash App</h3>
                                  <button
                                    type="button"
                                    onClick={() => setShowCashApp(false)}
                                    className="text-sm text-slate-500 hover:text-slate-700 underline"
                                    data-testid="cashapp-cancel"
                                  >
                                    Cancel
                                  </button>
                                </div>
                                {/* Prominent 2-step explainer — customers must
                                    reserve first, then pay via emailed steps. */}
                                <div className="rounded-lg border border-green-300 bg-green-50 p-3" data-testid="cashapp-howitworks">
                                  <p className="text-sm font-semibold text-green-900 flex items-center gap-1.5">
                                    <AlertCircle className="h-4 w-4" /> How this works — please read
                                  </p>
                                  <ol className="mt-2 space-y-1.5 text-xs text-green-900">
                                    <li className="flex gap-2"><span className="font-bold">1.</span><span><strong>Reserve first, pay second.</strong> Tap <strong>Reserve My Order</strong> below — this places your order and locks your price.</span></li>
                                    <li className="flex gap-2"><span className="font-bold">2.</span><span>You’ll immediately see your <strong>Cash App QR, exact amount, and note</strong> — and we’ll email them to you too.</span></li>
                                    <li className="flex gap-2"><span className="font-bold">3.</span><span>Then send your Cash App payment. <strong>If you pay without reserving first, we won’t receive your order.</strong></span></li>
                                  </ol>
                                </div>
                                {/* Payment details (QR, handle, note) shown AFTER
                                    the order is reserved — on the confirmation
                                    screen + email — so no one pays before we have
                                    their order. */}
                                <div className="rounded-lg bg-slate-50 p-3" data-testid="cashapp-amount">
                                  <p className="text-xs text-slate-500">Reservation total</p>
                                  <p className="font-bold text-green-700 text-lg">
                                    ${(() => {
                                      if (cashAppQuote && typeof cashAppQuote.total === 'number') {
                                        return Number(cashAppQuote.total).toFixed(2);
                                      }
                                      let t = finalTotal;
                                      if (user?.store_credit > 0 && useCredit) {
                                        const applied = creditAmount !== null
                                          ? creditAmount
                                          : Math.min(user.store_credit, finalTotal);
                                        t = Math.max(0, t - applied);
                                      }
                                      if (deliveryMethod === 'shipping') {
                                        t = Math.max(0, t - 10);
                                      }
                                      return Number(t || 0).toFixed(2);
                                    })()}
                                  </p>
                                  {deliveryMethod === 'shipping' && (
                                    <p className="text-xs text-green-600 mt-0.5">Includes $10 shipping discount</p>
                                  )}
                                  {cashAppQuote && cashAppQuote.discount_amount > 0 && (
                                    <p className="text-xs text-teal-700 mt-0.5">{appliedDiscount?.name || 'Member'} discount: -${cashAppQuote.discount_amount.toFixed(2)}</p>
                                  )}
                                </div>
                                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="cashapp-pay-after-note">
                                  <strong>Please don’t send payment yet.</strong> Tap <strong>Reserve My Order</strong> below first — then we’ll show your Cash App QR, the exact amount, and the note to include (and email them to you).
                                </div>

                                <Button
                                  type="button"
                                  onClick={handleConfirmCashAppSent}
                                  disabled={cashAppSubmitting}
                                  className="w-full h-12 bg-green-600 hover:bg-green-700 text-white"
                                  data-testid="cashapp-confirm-sent"
                                >
                                  {cashAppSubmitting ? (
                                    <span className="flex items-center gap-2">
                                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                                      Reserving...
                                    </span>
                                  ) : (
                                    "Reserve My Order"
                                  )}
                                </Button>
                                <p className="text-xs text-slate-500 text-center">
                                  We'll email you the exact amount and step-by-step payment instructions. This confirms your {siteSettings?.auto_cancel_stale_reservations_hours || 48}-hour reservation — your order is held until then.
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Zelle stopgap option — same flow as Cash App but
                            bank-to-bank. Toggled via admin Site Settings. */}
                        {zelleEnabled && (
                          <div className="mt-4" data-testid="zelle-payment-section">
                            {!showZelle ? (
                              <>
                                <div className="flex items-center gap-3 my-3">
                                  <div className="flex-1 h-px bg-slate-200" />
                                  <span className="text-xs uppercase tracking-wide text-slate-400">or</span>
                                  <div className="flex-1 h-px bg-slate-200" />
                                </div>
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => {
                                    if (!canCheckoutSync()) return;
                                    setZelleOrderId(`ZL-${Date.now().toString(36).toUpperCase().slice(-6)}`);
                                    setShowZelle(true);
                                  }}
                                  className="w-full h-14 border-2 border-purple-500 text-purple-700 hover:bg-purple-50 font-semibold flex flex-col items-center justify-center gap-0.5"
                                  data-testid="open-zelle-pay"
                                >
                                  <span className="flex items-center gap-2">
                                    <DollarSign className="h-4 w-4" />
                                    Pay with Zelle
                                  </span>
                                </Button>
                              </>
                            ) : (
                              <div
                                className="rounded-xl border-2 border-purple-500 bg-white p-4 space-y-3"
                                data-testid="zelle-pay-panel"
                              >
                                <div className="flex items-center justify-between">
                                  <h3 className="font-heading font-semibold text-slate-900">Pay with Zelle</h3>
                                  <button
                                    type="button"
                                    onClick={() => setShowZelle(false)}
                                    className="text-sm text-slate-500 hover:text-slate-700 underline"
                                    data-testid="zelle-cancel"
                                  >
                                    Cancel
                                  </button>
                                </div>
                                {/* Prominent 2-step explainer — customers must
                                    reserve first, then pay via emailed steps. */}
                                <div className="rounded-lg border border-purple-300 bg-purple-50 p-3" data-testid="zelle-howitworks">
                                  <p className="text-sm font-semibold text-purple-900 flex items-center gap-1.5">
                                    <AlertCircle className="h-4 w-4" /> How this works — please read
                                  </p>
                                  <ol className="mt-2 space-y-1.5 text-xs text-purple-900">
                                    <li className="flex gap-2"><span className="font-bold">1.</span><span><strong>Reserve first, pay second.</strong> Tap <strong>Reserve My Order</strong> below — this places your order and locks your price.</span></li>
                                    <li className="flex gap-2"><span className="font-bold">2.</span><span>You’ll immediately see your <strong>Zelle QR code, exact amount, and memo</strong> — and we’ll email them to you too.</span></li>
                                    <li className="flex gap-2"><span className="font-bold">3.</span><span>Then send your Zelle payment. <strong>If you pay without reserving first, we won’t receive your order.</strong></span></li>
                                  </ol>
                                </div>
                                {/* Payment details (QR, send-to email, memo) are shown
                                    AFTER the order is reserved — on the confirmation
                                    screen + email — so a customer can never send money
                                    before the order (and our notification) is created. */}
                                <div className="rounded-lg bg-slate-50 p-3" data-testid="zelle-amount">
                                  <p className="text-xs text-slate-500">Reservation total</p>
                                  <p className="font-bold text-purple-700 text-lg">
                                    ${(() => {
                                      if (zelleQuote && typeof zelleQuote.total === 'number') {
                                        return Number(zelleQuote.total).toFixed(2);
                                      }
                                      let t = finalTotal;
                                      if (user?.store_credit > 0 && useCredit) {
                                        const applied = creditAmount !== null
                                          ? creditAmount
                                          : Math.min(user.store_credit, finalTotal);
                                        t = Math.max(0, t - applied);
                                      }
                                      if (deliveryMethod === 'shipping') {
                                        t = Math.max(0, t - 10);
                                      }
                                      return Number(t || 0).toFixed(2);
                                    })()}
                                  </p>
                                  {deliveryMethod === 'shipping' && (
                                    <p className="text-xs text-purple-600 mt-0.5">Includes $10 shipping discount</p>
                                  )}
                                  {zelleQuote && zelleQuote.discount_amount > 0 && (
                                    <p className="text-xs text-teal-700 mt-0.5">{appliedDiscount?.name || 'Member'} discount: -${zelleQuote.discount_amount.toFixed(2)}</p>
                                  )}
                                </div>
                                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="zelle-pay-after-note">
                                  <strong>Please don’t send payment yet.</strong> Tap <strong>Reserve My Order</strong> below first — then we’ll show your Zelle QR code, the exact amount, and the memo to include (and email them to you).
                                </div>
                                {zelleNote && (
                                  <div className="rounded-lg bg-teal-50 border border-teal-200 p-3 text-sm text-teal-900" data-testid="zelle-note">
                                    {zelleNote}
                                  </div>
                                )}
                                {/* Customer's Zelle identity — needed so the
                                    team can send a Zelle REQUEST for the exact
                                    reserved amount. */}
                                <div className="rounded-lg border border-purple-300 bg-purple-50 p-3 space-y-2" data-testid="zelle-sender-fields">
                                  <p className="text-sm font-semibold text-purple-900">Your Zelle details</p>
                                  <p className="text-xs text-purple-800">
                                    Enter the name and phone number or email linked to your Zelle so we can send you a Zelle request for the exact amount.
                                  </p>
                                  <div>
                                    <label className="text-xs font-medium text-slate-600">Name on your Zelle account</label>
                                    <input
                                      type="text"
                                      value={zelleSenderName}
                                      onChange={(e) => setZelleSenderName(e.target.value)}
                                      placeholder="e.g. Jane Doe"
                                      className="mt-1 w-full rounded-lg border border-purple-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                      data-testid="zelle-sender-name"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs font-medium text-slate-600">Zelle phone number or email</label>
                                    <input
                                      type="text"
                                      value={zelleSenderContact}
                                      onChange={(e) => setZelleSenderContact(e.target.value)}
                                      placeholder="Phone or email linked to Zelle"
                                      className="mt-1 w-full rounded-lg border border-purple-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                      data-testid="zelle-sender-contact"
                                    />
                                  </div>
                                </div>
                                <Button
                                  type="button"
                                  onClick={handleConfirmZelleSent}
                                  disabled={zelleSubmitting}
                                  className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white"
                                  data-testid="zelle-confirm-sent"
                                >
                                  {zelleSubmitting ? (
                                    <span className="flex items-center gap-2">
                                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                                      Reserving...
                                    </span>
                                  ) : (
                                    "Reserve My Order"
                                  )}
                                </Button>
                                <p className="text-xs text-slate-500 text-center">
                                  This places your order now and emails you the QR code, exact amount, and payment steps. Your order is held for {siteSettings?.auto_cancel_stale_reservations_hours || 48} hours — then send your Zelle payment.
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="login-to-purchase">
                    <Link to="/auth">
                      <Button className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white">
                        <LogIn className="mr-2 h-4 w-4" />
                        Sign In to Purchase
                      </Button>
                    </Link>
                    <p className="text-xs text-slate-500 text-center">
                      An account is required to complete your purchase. <Link to="/auth" className="text-teal-700 hover:underline font-medium">Create one</Link> in seconds.
                    </p>
                  </div>
                )}

                {/* Research Notice */}
                <div className="mt-6 p-3 bg-amber-50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Shield className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700">
                      All products are for research purposes only. Not for human consumption.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
